import type {
  AskAutodorQueuesArgs,
  CreateAskAutodorQueueInput,
  createAskAutodorQueueMutation,
  CreateQueueInput,
  createQueueMutation,
  deleteAskAutodorQueueMutation,
  getAskAutodorQueueNamesQuery,
  getAskAutodorQueueQuery,
  getAskAutodorQueuesQuery,
  SaveCasesAsQueueInput,
  saveCasesAsQueueMutation,
  UpdateAskAutodorQueueInput,
  updateAskAutodorQueueMutation,
  UpdateQueueInput,
  updateQueueMutation,
} from "../../remote/gql/graphql";
import type { ClientReturnType } from "../types";
import type { AlaffiaClientModuleArgs } from "./modules";

import { createAskAutodorQueueMutationDoc } from "../../remote/mutations/createAskAutodorQueue.gql";
import { createQueueMutationDoc } from "../../remote/mutations/createQueue.gql";
import { deleteAskAutodorQueueMutationDoc } from "../../remote/mutations/deleteAskAutodorQueue.gql";
import { saveCasesAsQueueMutationDoc } from "../../remote/mutations/saveCasesAsQueue.gql";
import { updateAskAutodorQueueMutationDoc } from "../../remote/mutations/updateAskAutodorQueue.gql";
import { updateQueueMutationDoc } from "../../remote/mutations/updateQueue.gql";
import { getAskAutodorQueueQueryDoc } from "../../remote/queries/getAskAutodorQueue.gql";
import { getAskAutodorQueueNamesQueryDoc } from "../../remote/queries/getAskAutodorQueueNames.gql";
import { getAskAutodorQueuesQueryDoc } from "../../remote/queries/getAskAutodorQueues.gql";
import { AlaffiaClientModule } from "./modules";

type QueuesModuleArgs = AlaffiaClientModuleArgs;
export class QueuesModule extends AlaffiaClientModule {
  constructor(args: QueuesModuleArgs) {
    super(args);
  }

  createAskAutodorQueue = async (input: {
    input: CreateAskAutodorQueueInput;
  }): ClientReturnType<
    createAskAutodorQueueMutation["createAskAutodorQueue"]
  > => {
    const result = await this.runGqlMutation(
      createAskAutodorQueueMutationDoc,
      { input: input.input },
      "createAskAutodorQueue",
    );

    return {
      data: result.data?.createAskAutodorQueue ?? null,
      error: result.error ?? null,
    };
  };

  createQueue = async ({
    input,
  }: {
    input: CreateQueueInput;
  }): ClientReturnType<createQueueMutation["createQueue"]> => {
    const result = await this.runGqlMutation(
      createQueueMutationDoc,
      { input },
      "createQueue",
    );

    return {
      data: result.data?.createQueue ?? null,
      error: result.error ?? null,
    };
  };

  deleteAskAutodorQueue = async (input: {
    id: string;
  }): ClientReturnType<
    deleteAskAutodorQueueMutation["deleteAskAutodorQueue"]
  > => {
    const result = await this.runGqlMutation(
      deleteAskAutodorQueueMutationDoc,
      { id: input.id },
      "deleteAskAutodorQueue",
    );

    return {
      data: result.data?.deleteAskAutodorQueue ?? null,
      error: result.error ?? null,
    };
  };

  getAskAutodorQueueNames = async (): ClientReturnType<
    getAskAutodorQueueNamesQuery["askAutodorQueueNames"]
  > => {
    const result = await this.runGqlQuery(
      getAskAutodorQueueNamesQueryDoc,
      {},
      "getAskAutodorQueueNames",
    );

    return {
      data: result.data?.askAutodorQueueNames ?? null,
      error: result.error ?? null,
    };
  };

  getAskAutodorQueue = async (input: {
    id: string;
  }): ClientReturnType<getAskAutodorQueueQuery["askAutodorQueue"]> => {
    const result = await this.runGqlQuery(
      getAskAutodorQueueQueryDoc,
      { id: input.id },
      "getAskAutodorQueue",
    );
    return {
      data: result.data?.askAutodorQueue ?? null,
      error: result.error ?? null,
    };
  };

  getAskAutodorQueues = async (
    input: Pick<AskAutodorQueuesArgs, "filterArray">,
    paginationArgs?: Omit<AskAutodorQueuesArgs, "filterArray">,
  ): ClientReturnType<getAskAutodorQueuesQuery["askAutodorQueues"]> => {
    const result = await this.runGqlQuery(
      getAskAutodorQueuesQueryDoc,
      {
        args: {
          ...input,
          ...paginationArgs,
        },
      },
      "getAskAutodorQueues",
    );

    return {
      data: result.data?.askAutodorQueues ?? null,
      error: result.error ?? null,
    };
  };

  saveCasesAsQueue = async ({
    input,
  }: {
    input: SaveCasesAsQueueInput;
  }): ClientReturnType<saveCasesAsQueueMutation["saveCasesAsQueue"]> => {
    const result = await this.runGqlMutation(
      saveCasesAsQueueMutationDoc,
      { input },
      "saveCasesAsQueue",
    );

    return {
      data: result.data?.saveCasesAsQueue ?? null,
      error: result.error ?? null,
    };
  };

  updateAskAutodorQueue = async ({
    id,
    queueData,
  }: {
    id: string;
    queueData: UpdateAskAutodorQueueInput;
  }): ClientReturnType<
    updateAskAutodorQueueMutation["updateAskAutodorQueue"]
  > => {
    const result = await this.runGqlMutation(
      updateAskAutodorQueueMutationDoc,
      {
        id,
        input: {
          name: queueData.name,
        },
      },
      "updateAskAutodorQueue",
    );

    return {
      data: result.data?.updateAskAutodorQueue ?? null,
      error: result.error ?? null,
    };
  };

  updateQueue = async ({
    input,
  }: {
    input: UpdateQueueInput;
  }): ClientReturnType<updateQueueMutation["updateQueue"]> => {
    const result = await this.runGqlMutation(
      updateQueueMutationDoc,
      {
        input: input,
      },
      "updateQueue",
    );

    return {
      data: result.data?.updateQueue ?? null,
      error: result.error ?? null,
    };
  };
}
